import Analytics from 'analytics';
//@ts-ignore
import googleAnalytics from '@analytics/google-analytics'; // https://github.com/DavidWells/analytics/issues/107
import ReactPixel from 'react-facebook-pixel';
import TiktokPixel from './TiktokPixel';
import { IN_DEVELOPMENT } from '../constants';
import EVENTS from '../constants/events';
import * as braze from '@braze/web-sdk';
import debug from './debug';

import { centsToDollars } from '../libs/polygon-ordering/src/utils/misc';
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent as firebaseEvent } from 'firebase/analytics';
import { v4 as uuidv4 } from 'uuid';
import sha256 from 'js-sha256';
import TagManager from 'react-gtm-module';
import Api from '../libs/polygon-ordering/src/utils/Api';

function getCurrency() {
  const currency = 'AUD';

  return currency;
}

const saleTypeLabels = {
  110: 'Catering delivery',
  107: 'Catering',
  103: 'Delivery',
  100: 'Dine In',
  104: 'Drive Thru',
  102: 'Pick Up',
  105: 'Table Order',
  101: 'Take Away',
  106: 'Web Ordering',
};

// This is an identifier that Salesforce requires.
let sfmcContactKey: string | undefined;

const formatCurrency = (cents?: number | null) => {
  const amount = centsToDollars(cents);
  if (!amount) return cents;

  const result = `${amount < 0 ? '-' : ''}${Math.abs(amount).toFixed(2)}`;

  return result;
};

let enabledBrazeEvents: string[] | null;

const getSalesforceContactKey = async () => {
  const path = '/api/v1/salesforce/contactid';
  try {
    const response = await Api.fetch({ method: 'GET', path }, 'member');
    if (response.contactID) sfmcContactKey = response.contactID;
  } catch {
    return;
  }
};

function getInitialised() {
  const google = Boolean(window._GAnalytics);
  const facebook = Boolean(window._FBAnalytics);
  const tiktok = Boolean(window.ttq);
  const firebase = Boolean(window.firebase);
  const brazeAnalytics = Boolean(window.brazeAnalytics);
  const googleTagManager = Boolean(window.googleTagManager);
  const salesforce = Boolean(window.salesforce);

  const noAnalytics =
    !google &&
    !facebook &&
    !firebase &&
    !tiktok &&
    !googleTagManager &&
    !salesforce &&
    !brazeAnalytics;

  return {
    google,
    facebook,
    noAnalytics,
    firebase,
    tiktok,
    brazeAnalytics,
    googleTagManager,
    salesforce,
  };
}

function getContentIDs(items: any) {
  const content_ids = [];

  for (let i = 0; i < items.length; i++) {
    content_ids.push(`${items[i].id}`);
  }

  return content_ids?.length > 1 ? `[${content_ids.join(',')}]` : content_ids[0];
}

function getContent(items: any, provider: string) {
  const content = [];

  if (provider === 'facebook') {
    for (let i = 0; i < items.length; i++) {
      content.push(`{id:${items[i].id}, quantity:${items[i].quantity}}`);
    }

    return `[${content.join(',')}]`;
  }
  if (provider === 'google') {
    for (let i = 0; i < items.length; i++) {
      content.push({
        item_id: items[i].id,
        item_name: items[i].name,
        price: centsToDollars(items[i].totalPrice),
        currency: getCurrency(),
      });
    }
    return content;
  }

  if (provider === 'tiktok') {
    for (let i = 0; i < items.length; i++) {
      content.push({
        content_id: items[i].id,
        content_type: 'product',
        quantity: items[i].quantity,
        content_name: items[i].name,
        price: centsToDollars(items[i].totalPrice),
      });
    }
    return content;
  }
}

export function initAnalytics({
  googleAnalyticsKey,
  facebookAnalyticsKey,
  member,
  firebaseConfig,
  tiktokAnalyticsKey,
  brazeAnalyticsKey,
  brazeSdkEndpoint,
  brazeEventsEnabled,
}: {
  googleAnalyticsKey: string;
  facebookAnalyticsKey: string;
  member?: Member;
  firebaseConfig: FirebaseConfig;
  tiktokAnalyticsKey: string;
  brazeAnalyticsKey: string;
  brazeSdkEndpoint: string;
  brazeEventsEnabled: string[] | null;
}) {
  try {
    if (brazeEventsEnabled) enabledBrazeEvents = brazeEventsEnabled;
    const { google, facebook, tiktok, firebase, brazeAnalytics } = getInitialised();

    let tiktokAdvancedMatching: AdvancedMatching = {};
    let advancedMatching: AdvancedMatching = {};

    if (member) {
      advancedMatching.em = member.email;
      advancedMatching.fn = member.givenName;
      advancedMatching.ln = member.surname;
      tiktokAdvancedMatching.email = member.email;
      if (member.mobile) {
        tiktokAdvancedMatching.phone = member.mobile;
      }
    }

    if (firebase) {
      const app = initializeApp(firebaseConfig);
      getAnalytics(app);
    }

    if (tiktokAnalyticsKey && !tiktok) {
      TiktokPixel.init(tiktokAnalyticsKey, tiktokAdvancedMatching, {
        debug: IN_DEVELOPMENT ? true : false,
      });
      debug('initialised tiktok analytics', { tiktokAnalyticsKey });
    }
    if (googleAnalyticsKey && !google) {
      window._GAnalytics = Analytics({
        app: 'polygon-web-ordering',
        version: process.env.REACT_APP_VERSION,
        plugins: [
          googleAnalytics({
            measurementIds: [googleAnalyticsKey],
          }),
        ],
      });

      debug('initialised google analytics', { googleAnalyticsKey });
    }

    if (facebookAnalyticsKey && !facebook) {
      //@ts-ignore
      ReactPixel.init(facebookAnalyticsKey!, advancedMatching, {
        autoConfig: false, // https://developers.facebook.com/docs/facebook-pixel/advanced/
        debug: IN_DEVELOPMENT ? true : false, // logs
      });

      window._FBAnalytics = true;

      debug('initialised facebook analytics', { facebookAnalyticsKey });
    }

    if (brazeAnalyticsKey && !brazeAnalytics) {
      braze.initialize(brazeAnalyticsKey, {
        baseUrl: brazeSdkEndpoint,
        enableLogging: IN_DEVELOPMENT ? true : false,
      });

      braze.getDeviceId();

      braze.automaticallyShowInAppMessages();
      braze.openSession();
      braze.requestContentCardsRefresh();
      window.brazeAnalytics = true;
      window.braze = braze;
      debug('initialised braze analytics', { brazeAnalyticsKey });
    }
  } catch {
    return;
  }
}

export function initiateContentCards() {
  try {
    const { brazeAnalytics } = getInitialised();
    if (brazeAnalytics) {
      const { cards } = braze.getCachedContentCards();
      if (cards.length !== 0) {
        braze.toggleContentCards();
      }
    }
  } catch {
    return;
  }
}

export function logScreen(params: { screen: string; userId?: string }) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics, brazeAnalytics, googleTagManager } =
      getInitialised();

    const { screen, userId } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('SCREEN:', screen);
      return;
    }

    if (brazeAnalytics && enabledBrazeEvents?.includes('Screen Name')) {
      braze.logCustomEvent('Screen Name', {
        screenName: screen,
      });
    }

    if (tiktok) {
      TiktokPixel.pageView();
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'screen_view', {
        firebase_screen: screen!,
      });
    }

    if (google) {
      window._GAnalytics.page();
    }

    if (facebook) {
      ReactPixel.pageView();
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          event: 'user_parameter',
          user_type: userId ? 'Logged in' : 'Guest',
          user_id: userId || undefined,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}
export function logModal(modal: string | null) {
  try {
    const { noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('MODAL:', modal);
      return;
    }

    logEvent(modal ? EVENTS.OPEN_MODAL : EVENTS.CLOSE_MODAL, { label: modal });
  } catch {
    return;
  }
}

export async function removeUserSession() {
  const { noAnalytics, brazeAnalytics } = getInitialised();

  if (IN_DEVELOPMENT || noAnalytics) {
    debug('Wiping braze session data');
    return;
  }

  if (brazeAnalytics) {
    debug('Wiping braze session data');
    braze.wipeData(); //remove user session and data
  }
}

export async function logUser(userId?: string | null, data?: Member) {
  const userIdOrGuest = userId || 'GUEST';
  try {
    const { google, facebook, firebase, noAnalytics, brazeAnalytics, salesforce } =
      getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      const logParts: (string | Member)[] = [userIdOrGuest];

      if (data) {
        logParts.push(data);
      }

      debug('USER:', ...logParts);

      return;
    }

    // Setting the ContactKey to be used in Salesforce events
    if (salesforce && userId) {
      await getSalesforceContactKey();
    } else if (salesforce && !userId) {
      sfmcContactKey = undefined;
    }

    if (brazeAnalytics && userId) {
      //@ts-ignore
      braze.changeUser(sha256(data!.email.toLowerCase()));

      if (data?.email) {
        braze.getUser()?.setEmail(data!.email);
      }
      if (data?.givenName) {
        braze.getUser()?.setFirstName(data!.givenName);
      }

      if (data?.surname) {
        braze.getUser()?.setLastName(data!.surname);
      }

      if (data?.dateOfBirth) {
        const date = new Date(data.dateOfBirth);
        braze.getUser()?.setDateOfBirth(date.getFullYear(), date.getDate(), date.getDay());
      }

      if (data?.mobile) {
        braze.getUser()?.setPhoneNumber(data.mobile);
      } else if (data?.phone) {
        braze.getUser()?.setPhoneNumber(data.phone);
      }

      navigator.geolocation.getCurrentPosition(position => {
        const { coords } = position;
        braze
          .getUser()
          ?.setLastKnownLocation(
            coords.latitude,
            coords.longitude,
            coords.accuracy,
            coords.altitude ? coords.altitude : undefined,
            coords.altitudeAccuracy ? coords.altitudeAccuracy : undefined,
          );
      });

      const fields = Object.keys(data!).filter(
        item =>
          item === data?.email ||
          item === data?.givenName ||
          item === data?.surname ||
          item === data?.dateOfBirth ||
          item === data?.mobile ||
          item === data?.phone,
      );

      fields.map(field =>
        //@ts-ignore
        braze.getUser()?.setCustomUserAttribute(field, data![field as keyof Member]),
      ); // custom fields )
      initiateContentCards();
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      analytics.setUserId(userIdOrGuest);
    }

    if (google) {
      window._GAnalytics.identify(userIdOrGuest, data);
    }

    if (facebook) {
      // is there a corresponding function?
    }

    if (salesforce) {
      const salesforcePayload = {
        user: { id: data?.memberNumber, attributes: { sfmcContactKey, emailAddress: data?.email } },
        action: 'Login',
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

type LogEvent = {
  action: string;
  category: string;
};
type LogEventParams = { category?: string; label?: string; value?: string | number };
type LogEventData = { label?: string | null; value?: number | string };

export function logEvent(event: LogEvent, data?: LogEventData) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics, brazeAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      const logParts: (string | LogEventData)[] = [event.action];

      if (data) {
        logParts.push(data);
      }

      debug('EVENT:', ...logParts);

      return;
    }

    const params: LogEventParams = {};

    params.category = event.category;

    if (data?.label != null) {
      params.label = data.label;
    }

    if (data?.value != null) {
      params.value = data.value;
    }

    if (brazeAnalytics && enabledBrazeEvents?.includes(event.action)) {
      braze.logCustomEvent(event.action, params);
    }

    if (tiktok) {
      // No custom tracking
    }

    if (firebase) {
      const analytics = getAnalytics();
      firebaseEvent(analytics, event.action, params);
    }

    if (google) {
      window._GAnalytics.track(event.action, params);
    }

    if (facebook) {
      // More info about custom events:
      // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#custom-events
      ReactPixel.trackCustom(event.action, params);

      // More info about standard events:
      // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#standard-events
      // https://developers.facebook.com/docs/facebook-pixel/reference#standard-events
      // ReactPixel.track(event, data);
    }
  } catch {
    return;
  }
}

export function paymentInfo({
  items,
  total,
  payment_method,
  store,
}: {
  items: any;
  total: number;
  payment_method: PAYMENT_METHOD;
  store?: string;
}) {
  try {
    const { facebook, firebase, tiktok, noAnalytics, brazeAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('PaymentInfo:', { items, total, payment_method });
      return;
    }

    if (brazeAnalytics && enabledBrazeEvents?.includes('Payment Info')) {
      braze.logCustomEvent('Payment Info', {
        content_category: payment_method,
        contents: getContent(items, 'tiktok'),
        currency: getCurrency(),
        value: centsToDollars(total),
      });
    }

    if (tiktok) {
      TiktokPixel.track('AddPaymentInfo', {
        content_category: payment_method,
        contents: getContent(items, 'tiktok'),
        currency: getCurrency(),
        value: centsToDollars(total),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'add_payment_info', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
        payment_type: payment_method,
        store,
      });
    }

    if (facebook) {
      ReactPixel.track('AddPaymentInfo', {
        content_category: payment_method,
        content_ids: getContentIDs(items),
        currency: getCurrency(),
        value: centsToDollars(total),
        content_type: 'product',
      });
    }
  } catch {
    return;
  }
}

export function viewProductDetailPage({
  product,
  quantity,
  choicesWithQuantity,
  brand,
  category,
  member,
  store,
  saleType,
}: {
  product: Item;
  quantity: number | null;
  choicesWithQuantity: ChoiceWithQuantity[];
  brand: string | null;
  category: string | null;
  member?: Member;
  store?: string;
  saleType: SALE_TYPE;
}) {
  try {
    const { noAnalytics, googleTagManager, salesforce } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('ViewItem:', { product, quantity, choicesWithQuantity, brand, category, member });
      return;
    }

    //TODO add other analytics providers

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          event: 'dh_view_item',
          ecommerce: {
            _clear: true,
            transaction_id: undefined,
            value: formatCurrency(product.baseMoneyPrice),
            currency: getCurrency(),
            items: [
              {
                item_id: product.id || undefined,
                item_name: product.name || undefined,
                item_brand: brand || undefined,
                item_category: category || undefined,
                item_variant: choicesWithQuantity.length
                  ? choicesWithQuantity.map(option => ({
                      id: option.id,
                      name: option.name,
                      moneyPrice: formatCurrency(option.moneyPrice),
                      quantity: option.quantity,
                    }))
                  : undefined,
                price: formatCurrency(product.baseMoneyPrice) || undefined,
                quantity: quantity || undefined,
              },
            ],
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }

    if (salesforce) {
      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: { sfmcContactKey, emailAddress: member?.email },
        },
        interaction: {
          name: 'View Catalog Object',
          // product ID is required but composite items don't have IDs so we default to an 'undefined' string
          catalogObject: { type: 'Product', id: product.plucode || 'undefined' },
        },
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function addToCart({
  product,
  moneyPrice,
  quantity,
  choicesWithQuantity,
  brand,
  category,
  member,
  store,
  saleType,
  origin,
}: {
  product: Item;
  moneyPrice: number;
  quantity: number;
  choicesWithQuantity: ChoiceWithQuantity[];
  brand: string | null;
  category: string | null;
  member?: Member;
  store?: string;
  saleType: SALE_TYPE;
  origin: string;
}) {
  try {
    const {
      facebook,
      firebase,
      tiktok,
      noAnalytics,
      brazeAnalytics,
      googleTagManager,
      salesforce,
    } = getInitialised();
    if (IN_DEVELOPMENT || noAnalytics) {
      debug('AddtoCart:', {
        product,
        moneyPrice,
        quantity,
        choicesWithQuantity,
        brand,
        category,
        member,
      });
      return;
    }

    if (brazeAnalytics && enabledBrazeEvents?.includes('Add To Cart')) {
      braze.logCustomEvent('Add To Cart', {
        product,
        moneyPrice,
        quantity,
      });
    }

    if (tiktok) {
      TiktokPixel.track('AddToCart', {
        content_type: 'product',
        quantity,
        content_name: product.name,
        content_id: `${product.id}`,
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'add_to_cart', {
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            quantity,
            price: centsToDollars(moneyPrice),
            currency: getCurrency(),
          },
        ],
        store,
      });
    }

    if (facebook) {
      ReactPixel.track('AddToCart', {
        content_name: product.name, // name of the page
        content_type: 'product', // always product
        content_ids: [`${product.id}`], // array of numbers or strings
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
      });
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          event: 'dh_add_to_cart',
          ecommerce: {
            _clear: true,
            transaction_id: undefined,
            value: formatCurrency(moneyPrice),
            currency: getCurrency(),
            items: [
              {
                item_id: product.id || undefined,
                item_name: product.name || undefined,
                item_brand: brand || undefined,
                item_category: category || undefined,
                item_variant: choicesWithQuantity.length
                  ? choicesWithQuantity.map(option => ({
                      id: option.id,
                      name: option.name,
                      moneyPrice: formatCurrency(option.moneyPrice),
                      quantity: option.quantity,
                    }))
                  : undefined,
                price: formatCurrency(product.baseMoneyPrice) || undefined,
                quantity: quantity || undefined,
              },
            ],
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
          addingTypeProduct: origin || undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }

    if (salesforce) {
      const choicesTotal = choicesWithQuantity
        .map(cwq => cwq.baseMoneyPrice * cwq.quantity)
        .reduce((acc, current) => acc + current, 0);

      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: { sfmcContactKey, emailAddress: member?.email },
        },
        interaction: {
          name: 'Add To Cart',
          lineItem: {
            catalogObjectType: 'Product',
            catalogObjectId: product.plucode,
            quantity,
            price: ((product.baseMoneyPrice + choicesTotal) * quantity) / 100, // El Jannah expects this to be the total price, not the per-item price,
            currency: getCurrency(),
            attributes: {
              choices: choicesWithQuantity.map(choice => ({
                id: choice.plucode,
                name: choice.name,
                price: choice.baseMoneyPrice / 100,
                quantity: choice.quantity,
              })),
            },
          },
        },
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function removeFromCart(params: {
  product: Item;
  quantity: number;
  member?: Member;
  choicesWithQuantity: ChoiceWithQuantity[];
  moneyPrice: number;
  brand: string | null;
  category: string | null;
  store?: string;
  saleType: SALE_TYPE;
}) {
  try {
    const { noAnalytics, salesforce, googleTagManager } = getInitialised();

    const {
      product,
      quantity,
      member,
      choicesWithQuantity,
      moneyPrice,
      brand,
      category,
      store,
      saleType,
    } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('RemoveFromCart:', params);
      return;
    }

    if (salesforce) {
      const choicesTotal = choicesWithQuantity
        .map(cwq => cwq.baseMoneyPrice * cwq.quantity)
        .reduce((acc, current) => acc + current, 0);

      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: { sfmcContactKey, emailAddress: member?.email },
        },
        interaction: {
          name: 'Remove From Cart',
          lineItem: {
            catalogObjectType: 'Product',
            catalogObjectId: product.plucode,
            quantity,
            price: ((product.baseMoneyPrice + choicesTotal) * quantity) / 100, // El Jannah expects this to be the total price, not the per-item price,
            currency: getCurrency(),
            attributes: {
              choices: choicesWithQuantity.map(choice => ({
                id: choice.plucode,
                name: choice.name,
                price: choice.baseMoneyPrice / 100,
                quantity: choice.quantity,
              })),
            },
          },
        },
      };
      window.Evergage.sendEvent(salesforcePayload);
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          event: 'dh_remove_from_cart',
          ecommerce: {
            _clear: true,
            transaction_id: undefined,
            value: formatCurrency(moneyPrice),
            currency: getCurrency(),
            items: [
              {
                item_id: product.id || undefined,
                item_name: product.name || undefined,
                item_brand: brand || undefined,
                item_category: category || undefined,
                item_variant: choicesWithQuantity.length
                  ? choicesWithQuantity.map(option => ({
                      id: option.id,
                      name: option.name,
                      moneyPrice: formatCurrency(option.moneyPrice),
                      quantity: option.quantity,
                    }))
                  : undefined,
                price: formatCurrency(product.baseMoneyPrice) || undefined,
                quantity: quantity || undefined,
              },
            ],
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

/**
 * This is intended to be called whenever an item is added or removed from the cart.
 * addToCart and removeFromCart simply deal with the one item. updateCart
 * shows a snapshot of the entire cart.
 */
export function updateCart({
  member,
  cart,
  selectedOffer,
  menu,
}: {
  member?: Member;
  cart: CartItem[];
  selectedOffer?: Offer;
  menu?: Menu;
}) {
  try {
    const { salesforce, noAnalytics } = getInitialised();
    if (IN_DEVELOPMENT || noAnalytics) {
      debug('UpdateCart:', { member, cart, selectedOffer });
      return;
    }

    if (salesforce) {
      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: { sfmcContactKey, emailAddress: member?.email },
        },
        interaction: {
          name: 'Replace Cart',
          lineItems: cart.map(item => {
            // item.Price only returns the base price. Need to calculate the cost of choices here.
            const choicesTotal = item.Ingredients.map(
              ingredient => ingredient.Price * (ingredient.Qty || 0),
            ).reduce((accumulator, current) => accumulator + current, 0);
            const amount = (item.Price + choicesTotal) * item.Qty;
            // Need to include the choices in the payload
            const choices = item.Ingredients.map(choice => ({
              id: choice.PLUCode,
              name: menu?.items[choice.ProductID].name,
              price: choice.Price / 100,
              quantity: choice.Qty,
            }));
            return {
              catalogObjectType: 'Product',
              catalogObjectId: item.PLUCode,
              quantity: item.Qty,
              price: amount / 100,
              currency: getCurrency(),
              attributes: { choices },
            };
          }),
        },
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function viewCart(params: {
  items: any;
  total: number;
  store?: string;
  saleType: SALE_TYPE;
}) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    const { items, total, store, saleType } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('ViewCart:', params);
      return;
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          event: 'dh_view_cart',
          ecommerce: {
            _clear: true,
            transaction_id: undefined,
            value: formatCurrency(total),
            currency: getCurrency(),
            items: items.length
              ? items?.map(
                  (
                    item: Item & {
                      quantity: number;
                      choicesWithQuantity: any[];
                      brand: string | null;
                    },
                  ) => ({
                    item_id: item.id || undefined,
                    item_name: item.name || undefined,
                    item_brand: item.brand || undefined,
                    item_category: item.category || undefined,
                    item_variant: item.choicesWithQuantity.length
                      ? item.choicesWithQuantity.map(option => ({
                          id: option.id,
                          name: option.name,
                          moneyPrice: formatCurrency(option.moneyPrice),
                          quantity: option.quantity,
                        }))
                      : undefined,
                    price: formatCurrency(item.baseMoneyPrice) || undefined,
                    quantity: item.quantity || undefined,
                  }),
                )
              : undefined,
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function orderNow({ store, origin }: { store?: string; origin: string }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('ViewItem:', {});
      return;
    }

    //TODO add other analytics providers

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'dh_order_now',
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          ecommerce: undefined,
          saleType: undefined,
          store: store || undefined,
          clicked_item: origin || undefined,
          OrderType: undefined,
          order_type: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function saleTypeSelected({ saleType, store }: { saleType: SALE_TYPE; store?: string }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('ViewItem:', { saleType: saleTypeLabels[saleType] });
      return;
    }

    //TODO add other analytics providers

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'dh_order_type',
          saleType: saleTypeLabels[saleType],
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          ecommerce: undefined,
          store: store || undefined,
          order_type: saleTypeLabels[saleType] || undefined,
          OrderType: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function purchase({
  items,
  total,
  transactionId,
  brand,
  member,
  selectedOffer,
  store,
  saleType,
  purchaseType,
}: {
  items: any[];
  total: number;
  transactionId?: string;
  brand?: string;
  member?: Member;
  selectedOffer?: Offer;
  store?: string;
  saleType: SALE_TYPE;
  purchaseType: string;
}) {
  try {
    const { facebook, firebase, tiktok, noAnalytics, googleTagManager, salesforce } =
      getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('Purchase:', { items, total, transactionId, brand, member, selectedOffer });
      return;
    }

    if (tiktok) {
      TiktokPixel.track('CompletePayment', {
        contents: getContent(items, 'tiktok'),
        value: centsToDollars(total),
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'purchase', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
        transaction_id: uuidv4(), //  helps you avoid getting duplicate events for a purchase
        store,
      });
    }

    if (facebook) {
      ReactPixel.track('Purchase', {
        content_type: 'product', // always product
        content_ids: getContentIDs(items), // array of numbers or strings
        currency: getCurrency(),
        value: centsToDollars(total),
      });
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'dh_purchase',
          ecommerce: {
            transaction_id: transactionId,
            value: formatCurrency(total),
            currency: getCurrency(),
            items: items.length
              ? items.map((item: Item & { quantity: number; choicesWithQuantity: any[] }) => ({
                  item_id: item.id || undefined,
                  item_name: item.name || undefined,
                  item_brand: brand || undefined,
                  item_category: item.category || undefined,
                  item_variant: item.choicesWithQuantity
                    ? item.choicesWithQuantity.map(option => ({
                        id: option.id,
                        name: option.name,
                        moneyPrice: formatCurrency(option.moneyPrice),
                        quantity: option.quantity,
                      }))
                    : undefined,
                  price: formatCurrency(item.baseMoneyPrice) || undefined,
                  quantity: item.quantity || undefined,
                }))
              : undefined,
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
          purchase_type: purchaseType || undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }

    if (salesforce) {
      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: { sfmcContactKey, emailAddress: member?.email },
        },
        interaction: {
          name: 'Purchase',
          order: {
            id: transactionId,
            lineItems: items.map(
              (
                item: Item & { quantity: number; choicesWithQuantity: any[]; totalPrice: number },
              ) => ({
                catalogObjectType: 'Product',
                catalogObjectId: item.plucode,
                quantity: item.quantity,
                price: item.totalPrice / 100,
                currency: getCurrency(),
                attributes: {
                  choices: item.choicesWithQuantity.map(option => ({
                    id: option.plucode,
                    name: option.name,
                    price: option.moneyPrice / 100,
                    quantity: option.quantity,
                  })),
                },
              }),
            ),
            totalValue: total / 100,
            currency: getCurrency(),
            attributes: { coupon: { id: selectedOffer?.id, name: selectedOffer?.name } },
          },
        },
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function purchaseGiftCard(params: { store?: string; amount: number; saleType: SALE_TYPE }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    const { store, amount, saleType } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('PurchaseGiftCard:', {});
      return;
    }

    //TODO add other analytics providers

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'dh_purchase_gift_card',
          ecommerce: {
            transaction_id: undefined,
            value: amount,
            currency: getCurrency(),
            items: [
              {
                item_id: undefined,
                item_name: 'Gift card',
                item_brand: undefined,
                item_category: undefined,
                item_variant: undefined,
                price: amount,
                quantity: 1,
              },
            ],
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
          purchase_type: undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function registerUser({
  content_name,
  status,
  value,
  items,
  userData,
  store,
}: {
  content_name: string;
  status: boolean;
  value?: OrderTotals;
  items?: any;
  userData?: any;
  store?: string;
}) {
  try {
    const { facebook, firebase, tiktok, noAnalytics, googleTagManager, salesforce } =
      getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('RegisterUser:', content_name, status, value, items);
      return;
    }

    if (tiktok) {
      TiktokPixel.track('CompleteRegistration', {
        contents: getContent(items, 'tiktok'),
        content_name,
        value: centsToDollars(value?.discountedMoneyPrice) || 0,
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      firebaseEvent(analytics, 'sign_up', { method: content_name, store });
    }

    if (facebook) {
      ReactPixel.track('CompleteRegistration', {
        content_name,
        status,
        value: centsToDollars(value?.discountedMoneyPrice) || 0,
        currency: getCurrency(),
      });
    }

    if (googleTagManager && userData) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'signup_complete',
          UserID: userData?.ID,
          FirstName: userData?.GivenNames,
          LastName: userData?.Surname,
          Email: userData?.Email,
          PhoneNumber: userData?.Mobile,
          State: userData?.State,
          City: userData?.Suburb,
          ZipCode: userData?.Postcode,
          DateOfBirth: userData?.DateOfBirth,
          ecommerce: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: undefined,
          order_type: undefined,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }

    if (salesforce) {
      const salesforcePayload = {
        user: {
          id: userData?.MemberNo,
          attributes: { sfmcContactKey, emailAddress: userData?.Email },
        },
        action: 'Register User',
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function signInUser(params: {
  id: number;
  givenName: string;
  surname: string;
  email: string;
  phoneNumber: string;
  state: string;
  city: string;
  zipCode: string;
  dateOfBirth: string;
}) {
  const { noAnalytics, googleTagManager } = getInitialised();

  const { id, givenName, surname, email, phoneNumber, state, city, zipCode, dateOfBirth } = params;

  if (IN_DEVELOPMENT || noAnalytics) {
    debug('SignInUser:', params);
    return;
  }

  try {
    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'sign_in',
          UserID: id,
          FirstName: givenName,
          LastName: surname,
          Email: email,
          PhoneNumber: phoneNumber,
          State: state,
          City: city,
          ZipCode: zipCode,
          DateOfBirth: dateOfBirth,
          OrderType: undefined,
          order_type: undefined,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function initiateCheckout({
  items,
  total,
  member,
  store,
  saleType,
  selectedOffer,
}: {
  items: any;
  total: number;
  member?: Member;
  store?: string;
  saleType: SALE_TYPE;
  selectedOffer?: Offer;
}) {
  try {
    const { facebook, firebase, tiktok, noAnalytics, googleTagManager, salesforce } =
      getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('INITIATE CHECKOUT:', items, total);
      return;
    }

    if (tiktok) {
      TiktokPixel.track('InitiateCheckout', {
        contents: getContent(items, 'tiktok'),
        value: centsToDollars(total),
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'begin_checkout', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
        store,
      });
    }

    if (facebook) {
      ReactPixel.track('InitiateCheckout', {
        content_ids: getContentIDs(items),
        currency: getCurrency(),
        num_items: parseInt(items.length),
        value: centsToDollars(total),
        content_type: 'product',
      });
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'dh_begin_checkout',
          ecommerce: {
            transaction_id: undefined,
            value: formatCurrency(total),
            currency: getCurrency(),
            items: items.length
              ? items?.map(
                  (
                    item: Item & {
                      quantity: number;
                      choicesWithQuantity: any[];
                      brand: string | null;
                    },
                  ) => ({
                    item_id: item.id || undefined,
                    item_name: item.name || undefined,
                    item_brand: item.brand || undefined,
                    item_category: item.category || undefined,
                    item_variant: item.choicesWithQuantity.length
                      ? item.choicesWithQuantity.map(option => ({
                          id: option.id,
                          name: option.name,
                          moneyPrice: formatCurrency(option.moneyPrice),
                          quantity: option.quantity,
                        }))
                      : undefined,
                    price: formatCurrency(item.baseMoneyPrice) || undefined,
                    quantity: item.quantity || undefined,
                  }),
                )
              : undefined,
          },
          UserID: undefined,
          FirstName: undefined,
          LastName: undefined,
          Email: undefined,
          PhoneNumber: undefined,
          State: undefined,
          City: undefined,
          ZipCode: undefined,
          DateOfBirth: undefined,
          saleType: undefined,
          store: store || undefined,
          OrderType: saleTypeLabels[saleType] || undefined,
          order_type: undefined,
          coupon: selectedOffer?.name || undefined,
        },
      };

      TagManager.dataLayer(tagManagerArgs);
    }

    if (salesforce) {
      const salesforcePayload = {
        user: {
          id: member?.memberNumber,
          attributes: {
            sfmcContactKey,
            emailAddress: member?.email,
          },
        },
        action: 'Initiate Checkout',
        cart: {
          lineItems: items.map(
            (
              item: Item & { quantity: number; choicesWithQuantity: any[]; totalPrice: number },
            ) => ({
              catalogObjectType: 'Product',
              catalogObjectId: item.plucode,
              quantity: item.quantity,
              price: item.totalPrice / 100,
              currency: getCurrency(),
              attributes: {
                choices: item.choicesWithQuantity.map(option => ({
                  id: option.plucode,
                  name: option.name,
                  price: option.moneyPrice / 100,
                  quantity: option.quantity,
                })),
              },
            }),
          ),
        },
      };

      window.Evergage.sendEvent(salesforcePayload);
    }
  } catch {
    return;
  }
}

export function storeSelected(params: { storeName: string; saleType: SALE_TYPE }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    const { storeName, saleType } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('StoreSelected:', params);
      return;
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'store_selection',
          store_name: storeName || undefined,
          OrderType: undefined,
          order_type: saleTypeLabels[saleType] || undefined,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function deliveryAddressSelected(params: { success: boolean }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('DeliveryAddressSelected', params);
      return;
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'delivery_address_updated',
          address_status: params.success ? 'Success' : 'Failure',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function purchaseGiftCardClicked() {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('PurchaseGiftCardClicked');
      return;
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'purchase_gift_card_click',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}

export function menuCategorySelected(params: { categoryName: string }) {
  try {
    const { noAnalytics, googleTagManager } = getInitialised();

    const { categoryName } = params;

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('MenuCategorySelected', params);
      return;
    }

    if (googleTagManager) {
      const tagManagerArgs = {
        dataLayer: {
          _clear: true,
          event: 'menu_category_click',
          menu_category: categoryName || undefined,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  } catch {
    return;
  }
}
